import { useToast } from '@contexts/Toast';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  ContactUsIssueOptions,
  errorMessage,
  fileUploadConfig,
} from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { ContactSupportModalProps } from '@helpers/Models';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

export const ContactSupportModalModal: React.FC<ContactSupportModalProps> = ({
  onClose,
}) => {
  const { showToastMessage } = useToast();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    issue_type: '',
    message: '',
    imagefile: null as File | null,
  });

  /**
   * Handles changes in form input fields and updates the form state.
   *
   * - Extracts the `name` and `value` from the event target.
   * - Dynamically updates the corresponding field in the formData state.
   *
   * @function handleChange
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>} e - The input change event.
   * @returns {void}
   *
   * @dependencies
   * - setFormData: State setter used to update form data.
   */
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Handles file selection and validates the uploaded file.
   *
   * - Extracts the selected file from the input event.
   * - Validates file size against the maximum allowed limit.
   * - Validates file format (only .jpg and .png allowed).
   * - Updates state with the valid file.
   * - Displays appropriate error messages for invalid file selection.
   *
   * @function handleFileChange
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event.
   * @returns {void}
   *
   * @dependencies
   * - fileUploadConfig.MAX_FILE_SIZE: Maximum allowed file size.
   * - errorMessage: Contains predefined error messages.
   * - showToastMessage: Utility to display error messages.
   * - setFile: State setter to store the selected file.
   *
   * @throws Will show an error toast if file selection or validation fails.
   */
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    try {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        if (selectedFile.size > fileUploadConfig.MAX_FILE_SIZE) {
          showToastMessage(errorMessage.MAX_FILE_SIZE_ERROR, 'error');
        } else if (!/\.(jpg|png)$/i.test(selectedFile.name)) {
          showToastMessage(errorMessage.FILE_FORMATS, 'error');
        } else {
          setFile(selectedFile);
        }
      }
    } catch {
      showToastMessage(errorMessage.FILE_SELECTION_ERROR, 'error');
    }
  };

  /**
   * Handles changes in the reCAPTCHA token.
   *
   * - Updates the captcha token state whenever the user completes or resets the captcha.
   *
   * @function handleCaptchaChange
   * @param {string | null} token - The reCAPTCHA token received on verification, or null if expired/reset.
   * @returns {void}
   *
   * @dependencies
   * - setCaptchaToken: State setter used to store the captcha token.
   */
  const handleCaptchaChange = (token: string | null): void => {
    setCaptchaToken(token);
  };

  /**
   * Extracts and formats a user-friendly error message from an API error object.
   *
   * - Checks for structured backend validation errors (array of errors).
   * - If present, concatenates all error messages into a single string.
   * - Falls back to generic error message fields if structured errors are unavailable.
   *
   * @function getErrorMessage
   * @param {any} error - The error object received from an API response or exception.
   * @returns {string} A formatted error message string for display.
   *
   * @dependencies
   * - Relies on backend error structure: { errors: [{ key, message }] }
   */
  const getErrorMessage = (error: any): string => {
    // Check if backend returned structured errors
    const structuredErrors = error?.response?.data?.errors;

    if (Array.isArray(structuredErrors) && structuredErrors.length > 0) {
      return structuredErrors
        .map((err: { key: string; message: string }) => {
          return `${err.message}`;
        })
        .join(', ');
    }
    // Fallbacks
    return (
      error?.response?.data?.message || error?.message || 'Something went wrong'
    );
  };

  /**
   * Handles file verification and upload for the contact form.
   *
   * - Checks if a file is selected before proceeding.
   * - Uploads the file to the CONTACT_US_IMAGE_UPLOAD API using multipart/form-data.
   * - Returns the uploaded image URL on successful upload.
   * - Displays an error toast message if the upload fails.
   *
   * @async
   * @function fileVerification
   * @returns {Promise<string | void>} Returns the uploaded image URL if successful, otherwise undefined.
   *
   * @dependencies
   * - file: The file selected by the user for upload.
   * - Axios: HTTP client used for API request.
   * - ApiEndpoints.CONTACT_US_IMAGE_UPLOAD: API endpoint for image upload.
   * - showToastMessage: Utility to display error messages.
   * - getErrorMessage: Utility to extract error message from API response.
   */
  const fileVerification = async (): Promise<any> => {
    // Check if file exists
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await Axios.post(
        ApiEndpoints.CONTACT_US_IMAGE_UPLOAD,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response.data.data.imageUrl;
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  /**
   * Handles the submission of the contact form.
   *
   * - Validates required form fields (name, email, issue type, message).
   * - Verifies captcha token before proceeding.
   * - Optionally uploads and validates an attached file.
   * - Constructs the request payload and sends it to the CONTACT_US API.
   * - Displays success or error toast messages based on API response.
   * - Resets captcha and closes the form after submission.
   *
   * @async
   * @function handleSubmit
   * @returns {Promise<void>} Does not return any value.
   *
   * @throws Will show an error toast if API request fails.
   *
   * @dependencies
   * - formData: Object containing user input fields (name, email, issue_type, message).
   * - file: Optional file input for upload.
   * - fileVerification: Function to validate and upload file, returns image URL.
   * - captchaToken: Google reCAPTCHA token for validation.
   * - Axios: HTTP client used for API request.
   * - ApiEndpoints.CONTACT_US: API endpoint for form submission.
   * - showToastMessage: Utility to display success/error messages.
   * - getErrorMessage: Utility to extract error message from API response.
   * - recaptchaRef: Reference to reCAPTCHA component for reset.
   * - setCaptchaToken: State setter to clear captcha token.
   * - onClose: Callback to close the form/modal after submission.
   */
  const handleSubmit = async (): Promise<void> => {
    let imageUrl = '';

    // Upload the file only if theres any selected file
    if (file) {
      const result = await fileVerification();
      if (result) {
        imageUrl = result;
      }
    }
    // Field required validation
    if (
      !formData.name ||
      !formData.email ||
      !formData.issue_type ||
      !formData.message
    ) {
      showToastMessage(errorMessage.FORM_REQUIRED, 'error');
      return;
    }
    // reCaptcha required validation
    if (!captchaToken) {
      showToastMessage(errorMessage.REQUIRED_CAPTCHA, 'error');
      return;
    }
    // Create a payload
    const payload = {
      name: formData.name,
      email: formData.email,
      issue_type: formData.issue_type,
      message: formData.message,
      google: captchaToken ? captchaToken : '',
      imagefile: imageUrl ? imageUrl : '',
    };

    try {
      const response = await Axios.post(ApiEndpoints.CONTACT_US, payload);
      showToastMessage(response.data.message, 'success');
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }

    // Reset captcha after submission
    recaptchaRef.current?.reset();
    setCaptchaToken(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4 overflow-y-auto max-h-[95vh]">
        {/* Header */}
        <div className="relative flex items-center justify-center px-6 pt-6 pb-2">
          <img
            alt="Wild Game Butcher Logo"
            src={Assets.LOGO}
            className="h-16 object-contain"
          />
          <button
            type="button"
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            onClick={onClose}
            title="Close"
          >
            <FontAwesomeIcon icon={faClose} className="text-lg" />
          </button>
        </div>

        {/* Form card */}
        <div className="mx-4 mb-6 border border-gray-200 rounded-lg">
          <div className="px-4 pt-4 pb-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-[#1C1B1F]">
              Contact Support
            </h2>
          </div>

          <div className="px-6 py-6 space-y-6">
            <p className="text-[#4D545C]">
              Have an issue? Fill out the form and we&apos;ll get back to you
            </p>

            {/* Name */}
            <div className="">
              <label className="block text-sm text-[#1C1B1F] mb-1">
                Your Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-[#00000026] rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            {/* E-Mail */}
            <div>
              <label className="block text-sm text-[#1C1B1F] mb-1">
                E - Mail *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-[#00000026] rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Issue Type */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Issue Type *
              </label>
              <select
                name="issue_type"
                value={formData.issue_type}
                onChange={handleChange}
                className="w-full border border-[#00000026] rounded-lg px-3 py-2 text-sm text-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                }}
              >
                <option value="" disabled>
                  Select an issue
                </option>
                {ContactUsIssueOptions.map((options) => (
                  <>
                    <option value={options.value}>{options.option}</option>
                  </>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Please describe your issue in detail."
                rows={5}
                className="w-full border border-[#00000026] rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
              />
            </div>

            {/* Attach imagefile */}
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                Attach imagefile (optional)
              </label>
              <label className="inline-block cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <span className="px-5 py-2 border border-primary text-primary text-sm font-semibold rounded-md">
                  {file ? file.name : 'Upload File'}
                </span>
              </label>
            </div>

            {/* Google reCAPTCHA */}
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={
                process.env.REACT_APP_RECAPTCHA_SITE_KEY ??
                '6LdHvNssAAAAAGqCGZAe_tZjriLAts3R0fA09v6h'
              }
              onChange={handleCaptchaChange}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />

            {/* Submit */}
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full bg-primary text-white font-semibold py-3 rounded-lg text-sm"
            >
              Submit Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
